import { useEffect, useState } from "react";
import { Loader2, RefreshCcw, Truck, Send, AlertTriangle, CheckCircle2, Store, PackageOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { fetchShipStationStatus, updateShipStationSettings, shipStationSyncNow, shipStationPushOrder, fetchBackorders, releaseBackorder } from "@/lib/api";

const when = (s) => (s ? new Date(s).toLocaleString() : "—");
const ACTION_TONE = { shipped: "bg-emerald-50 text-emerald-700 border-emerald-200", pushed: "bg-sky-50 text-sky-700 border-sky-200", updated: "bg-sky-50 text-sky-700 border-sky-200", adopted: "bg-violet-50 text-violet-700 border-violet-200", error: "bg-rose-50 text-rose-700 border-rose-200", skipped: "bg-zinc-50 text-zinc-600 border-zinc-200" };

function Stat({ label, value, tone = "text-zinc-900", testId }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white px-3 py-2 min-w-0" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 truncate">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testId }) {
  return (
    <label className="flex items-start justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2.5 cursor-pointer">
      <div><div className="text-sm font-semibold text-zinc-900">{label}</div><div className="text-[11px] text-zinc-500">{hint}</div></div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </label>
  );
}

export default function ShipStationTab({ callerUserRecordId: caller }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pushNum, setPushNum] = useState("");
  const [pushing, setPushing] = useState(false);
  const [pushUpdate, setPushUpdate] = useState(false);

  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    try { setData(await fetchShipStationStatus(caller)); } catch (e) { toast.error(e?.response?.data?.detail || "Could not load ShipStation status"); } finally { setLoading(false); }
  }
  useEffect(() => { if (caller) load(); /* eslint-disable-next-line */ }, [caller]);

  async function setSetting(k, v) {
    try { const r = await updateShipStationSettings(caller, { [k]: v }); setData((d) => ({ ...d, settings: r.settings })); toast.success(v ? `${k === "push_enabled" ? "Push" : "Pull"} enabled` : `${k === "push_enabled" ? "Push" : "Pull"} disabled`); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function syncNow() {
    setSyncing(true);
    try {
      const r = await shipStationSyncNow(caller);
      toast.success(`Sync done in ${r.duration_sec}s · ${r.shipped?.length || 0} shipped · ${r.pushed?.length || 0} pushed · ${r.errors?.length || 0} errors`);
      await load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); } finally { setSyncing(false); }
  }
  async function pushOne() {
    if (!/^(B-)?\d+$/i.test(pushNum)) return;
    setPushing(true);
    try { const r = await shipStationPushOrder(caller, pushNum.toUpperCase(), pushUpdate); toast.success(`Order #${r.order_id} ${r.action}${r.items ? ` (${r.items} items)` : ""} · ShipStation ID ${r.ss_order_id}`); setPushNum(""); await load(true); } catch (e) { toast.error(e?.response?.data?.detail || "Push failed"); } finally { setPushing(false); }
  }

  if (loading && !data) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>;
  const s = data?.settings || {};
  const lr = data?.last_run;
  return (
    <div className="space-y-3" data-testid="shipstation-tab">
      {!data?.configured && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2" data-testid="ss-not-configured"><AlertTriangle size={14} /> ShipStation API key/secret are not configured on the server.</div>
      )}
      <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><Truck size={18} className="text-violet-600" /> ShipStation sync</h3>
            <div className="text-xs text-zinc-500 flex items-center gap-1" data-testid="ss-store"><Store size={12} /> {data?.store?.name ? `Store: ${data.store.name} (#${data.store.id})` : data?.store?.error ? `Store lookup failed: ${data.store.error}` : "Store: default"} · every {Math.round((s.interval_sec || 180) / 60)} min</div>
          </div>
          <Button onClick={syncNow} disabled={syncing || !data?.configured || data?.running} className="h-10 rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="ss-sync-now">
            {syncing || data?.running ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <RefreshCcw size={15} className="mr-1.5" />} Sync now
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Last sync" value={lr ? new Date(lr.finished_at).toLocaleTimeString() : "—"} testId="ss-last-sync" />
          <Stat label="Shipped → Airtable" value={lr?.shipped?.length ?? 0} tone="text-emerald-700" testId="ss-last-shipped" />
          <Stat label="Pushed → ShipStation" value={lr?.pushed?.length ?? 0} tone="text-sky-700" testId="ss-last-pushed" />
          <Stat label="Errors" value={lr?.errors?.length ?? 0} tone={lr?.errors?.length ? "text-rose-600" : "text-zinc-900"} testId="ss-last-errors" />
        </div>
        {lr && (
          <div className="text-[11px] text-zinc-500 flex items-center gap-1" data-testid="ss-last-run-line">
            {lr.ok ? <CheckCircle2 size={12} className="text-emerald-600" /> : <AlertTriangle size={12} className="text-rose-600" />}
            {lr.ok ? "OK" : `Failed: ${lr.error}`} · {lr.trigger} · {when(lr.finished_at)} · {lr.duration_sec}s
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <Toggle label="Pull shipments → Airtable" hint="When ShipStation ships an order: status completed + tracking, carrier, service, ship date." checked={!!s.pull_enabled} onChange={(v) => setSetting("pull_enabled", v)} testId="ss-toggle-pull" />
        <Toggle label="Push Processing orders → ShipStation" hint="Keep OFF while the Airtable automation is still on. Adopts existing ShipStation orders by number — never duplicates." checked={!!s.push_enabled} onChange={(v) => setSetting("push_enabled", v)} testId="ss-toggle-push" />
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-3 flex flex-wrap items-center gap-2">
        <Send size={14} className="text-zinc-500" />
        <span className="text-xs text-zinc-600">Test push a single order</span>
        <Input value={pushNum} onChange={(e) => setPushNum(e.target.value.replace(/[^0-9bB-]/g, ""))} placeholder="Order # e.g. 36253 or B-36390" className="h-9 w-44 rounded-lg" data-testid="ss-push-number" />
        <Button size="sm" variant="outline" disabled={pushing || !pushNum} onClick={pushOne} className="h-9 rounded-lg" data-testid="ss-push-btn">{pushing ? <Loader2 size={14} className="animate-spin" /> : pushUpdate ? "Update in ShipStation" : "Push to ShipStation"}</Button>
        <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer" title="Already in ShipStation? Replace its items/amounts with what is on the order now (e.g. after a split).">
          <input type="checkbox" checked={pushUpdate} onChange={(e) => setPushUpdate(e.target.checked)} className="accent-zinc-900" data-testid="ss-push-update" /> Update if already in ShipStation
        </label>
      </div>

      <BackordersSection caller={caller} />

      <div className="rounded-2xl border border-zinc-200 bg-white divide-y divide-zinc-100" data-testid="ss-log">
        <div className="px-3 py-2 text-xs font-semibold text-zinc-700">Sync log</div>
        {!data?.log?.length ? <div className="py-8 text-center text-xs text-zinc-500" data-testid="ss-log-empty">Nothing synced yet.</div>
          : data.log.map((l) => (
            <div key={l.id} className="px-3 py-2 flex items-start gap-3" data-testid={`ss-log-${l.order_id}-${l.action}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-zinc-900">#{l.order_id}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full border px-1.5 py-0.5 ${ACTION_TONE[l.action] || ACTION_TONE.skipped}`}>{l.action}</span>
                  <span className="text-[10px] text-zinc-400 uppercase">{l.direction}</span>
                </div>
                <div className="text-[11px] text-zinc-500 break-words">
                  {l.action === "shipped" ? `${l.carrier || ""} · ${l.tracking} · shipped ${l.ship_date}${l.prev_status ? ` · was ${l.prev_status}` : ""}` : l.error ? l.error : l.ss_order_id ? `ShipStation order ${l.ss_order_id}` : l.reason || ""}
                </div>
              </div>
              <div className="text-[11px] text-zinc-400 shrink-0">{when(l.at)}</div>
            </div>
          ))}
      </div>
    </div>
  );
}

function BackordersSection({ caller }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => fetchBackorders(caller).then((r) => setRows(r.items || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [caller]); // eslint-disable-line react-hooks/exhaustive-deps
  const release = async (r) => {
    if (!window.confirm(`Release ${r.order_ref} (${r.name}) and push it to ShipStation now?`)) return;
    setBusy(r.record_id);
    try {
      const res = await releaseBackorder(r.record_id, caller, true);
      const a = res.result?.action;
      if (a === "error") toast.error(`Released, but ShipStation push failed: ${res.result.error}`); else toast.success(`${r.order_ref} released — ShipStation: ${a || "ok"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); } finally { setBusy(""); }
  };
  const isWaiting = (r) => r.status.toLowerCase() === "backorder";
  const waiting = (rows || []).filter(isWaiting);
  const released = (rows || []).filter((r) => !isWaiting(r));
  return (
    <div className="rounded-2xl border border-amber-200 bg-white" data-testid="ss-backorders">
      <div className="px-3 py-2 border-b border-amber-100 flex flex-wrap items-center gap-2 bg-amber-50/60 rounded-t-2xl">
        <PackageOpen size={14} className="text-amber-700" />
        <span className="text-sm font-semibold text-amber-900">Back orders</span>
        <span className="text-xs text-amber-800/80">— split from a paid order (B-#). Held from ShipStation until you release them.</span>
        <span className="ml-auto text-xs font-semibold text-amber-800" data-testid="ss-backorders-count">{waiting.length} waiting</span>
      </div>
      {rows === null ? <div className="py-6 text-center text-xs text-zinc-500"><Loader2 size={14} className="animate-spin inline" /></div>
        : rows.length === 0 ? <div className="py-6 text-center text-xs text-zinc-500" data-testid="ss-backorders-empty">No back orders. Open an order and use <b>Split</b> to create one.</div>
        : (
          <ul className="divide-y divide-zinc-100">
            {[...waiting, ...released].map((r) => (
              <li key={r.record_id} className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`ss-backorder-${r.order_ref}`}>
                <span className="font-bold text-zinc-900">{r.order_ref}</span>
                <span className="text-xs text-zinc-500">from #{r.parent_post_id} · {r.date || ""}</span>
                <span className="text-zinc-800 truncate max-w-[180px]">{r.name}</span>
                <span className="text-xs text-zinc-500 truncate flex-1 min-w-[120px]" title={r.items}>{r.items}</span>
                <span className="text-xs font-semibold text-zinc-700">${Number(r.total || 0).toFixed(2)}</span>
                <span className={`text-[11px] font-semibold rounded-md border px-1.5 py-0.5 ${isWaiting(r) ? "bg-amber-50 text-amber-800 border-amber-200" : r.tracking ? ACTION_TONE.shipped : ACTION_TONE.pushed}`}>
                  {isWaiting(r) ? "backorder" : r.tracking ? `shipped · ${r.tracking}` : r.ss_order_id ? "in ShipStation" : r.status}
                </span>
                {isWaiting(r) && (
                  <Button size="sm" disabled={busy === r.record_id} onClick={() => release(r)} className="h-8 rounded-lg bg-amber-600 hover:bg-amber-700 text-xs" data-testid={`ss-release-${r.order_ref}`}>
                    {busy === r.record_id ? <Loader2 size={12} className="animate-spin" /> : <><Send size={12} className="mr-1" /> Release &amp; push</>}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

